<template>
    <div class="g-map-wrapper">
      <!-- 地图区域 -->
      <div id="map-view-preview" :style="{ width: '100%', height: '100%' }" >
      </div>
    </div>
  </template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { gcj02towgs84, wgs84togcj02 } from '/@/vendors/coordtransform'
import AMapLoader from '@amap/amap-jsapi-loader'
import { AMapConfig } from '/@/constants/index'
import { CURRENT_CONFIG } from '/@/api/http/config'
import { getOfflineBzImg, getOfflineWxImg, loadOfflineScript, offlineSite } from '/@/utils/map-layer-utils'
interface Markers {
  name: string,
  marker: any
}
const props = defineProps<{
    mapInfo:any,
}>()
const aMapView = ref()
const MapViewContainer = ref()
const markers = [] as Markers[]
const infoWindow = ref()

watch(() => props.mapInfo, (newValue, oldValue) => {
  removeMarker(oldValue?.file_name)
  if (isValidPoint(newValue?.lng, newValue?.lat)) {
    const point = wgs84togcj02(Number(newValue.lng), Number(newValue.lat))
    initInfoWindow(newValue.object_keys, point)
  }
}, {
  deep: true
})
onMounted(() => {
  initMap('map-view-preview', 15)
})
onUnmounted(() => {
  aMapView.value = null
  MapViewContainer.value.destroy()
  MapViewContainer.value = null
})
function initInfoWindow (content:string, position:Array<number>) {
  if (!MapViewContainer.value || !aMapView.value || !position || position.length < 2) {
    return
  }
  if (!Number.isFinite(position[0]) || !Number.isFinite(position[1])) {
    return
  }
  if (content.indexOf('.mp4') !== -1) {
    infoWindow.value?.close()
    return
  }
  const AMap = aMapView.value
  infoWindow.value.setContent(`<img src="${content}" width="40px" height="40px" />`)
  infoWindow.value.open(MapViewContainer.value, new AMap.LngLat(position[0], position[1]))
}
async function initMap (container: string, zoom?:number) {
  const online = !offlineSite.includes(window.location.origin)
  const onOperation = (AMap, layers) => {
    aMapView.value = AMap
    const centerPoint = CURRENT_CONFIG.mapCenter
    MapViewContainer.value = new AMap.Map(container, {
      center: wgs84togcj02(centerPoint[0], centerPoint[1]),
      zoom: zoom || 15,
      layers: layers
    })
    infoWindow.value = new AMap.InfoWindow({ offset: new AMap.Pixel(0, -30) })
    if (isValidPoint(props.mapInfo?.lng, props.mapInfo?.lat)) {
      const point = wgs84togcj02(Number(props.mapInfo.lng), Number(props.mapInfo.lat))
      initInfoWindow(props.mapInfo.object_keys, point)
    }
  }
  if (online) {
    window._AMapSecurityConfig = {
      securityJsCode: CURRENT_CONFIG.securityJsCode,
    }
    AMapLoader.load({
      ...AMapConfig
    }).then((AMap) => {
      onOperation(AMap, [AMap.createDefaultLayer(), new AMap.TileLayer.Satellite(), new AMap.TileLayer.RoadNet()])
    }).catch(e => {
      console.error(e)
    })
  } else {
    await loadOfflineScript('./amap/AMap3.js')
    const AMap = window.AMap
    const satelliteLayer = new AMap.TileLayer({
      getTileUrl: (x:string, y:string, z:string) => getOfflineWxImg(x, y, z),
      zIndex: 10
    })
    const labelLayer = new AMap.TileLayer({
      getTileUrl: (x:string, y:string, z:string) => getOfflineBzImg(x, y, z),
      zIndex: 11
    })
    onOperation(AMap, [satelliteLayer, labelLayer])
  }
}
function isValidPoint (lng: unknown, lat: unknown): boolean {
  const lngNum = Number(lng)
  const latNum = Number(lat)
  if (!Number.isFinite(lngNum) || !Number.isFinite(latNum)) {
    return false
  }
  if (lngNum === 0 || latNum === 0) {
    return false
  }
  return true
}

function initMarker (url: string, name: string, lng?: number, lat?: number) {
  if (aMapView.value === undefined) {
    return
  }
  const AMap = aMapView.value
  const markerImg = new AMap.Marker({
    position: new AMap.LngLat(lng || 113.943225499, lat || 22.577673716),
    icon: initIcon('//a.amap.com/jsapi_demos/static/demo-center/icons/poi-marker-default.png'),
    title: name,
    anchor: 'top-center',
    offset: [0, -20],
  })

  markers.push({
    name: name,
    marker: markerImg
  })
  MapViewContainer.value.add(markerImg)
}
function removeMarker (name: string) {
  markers.forEach(el => {
    if (name === el.name) {
      MapViewContainer.value.remove(el.marker)
      // delete markers[sn]
    }
  })
}
function initIcon (url:string) {
  const AMap = aMapView.value
  return new AMap.Icon({
    image: url,
    imageSize: new AMap.Size(40, 40),
    size: new AMap.Size(40, 40)
  })
}

// 获得多个点位的中心点
function getPointsCenter (points:any[]) {
  const center = [0, 0]
  points.forEach((item:any) => {
    center[0] += item[0]
    center[1] += item[1]
  })
  center[0] = center[0] / points.length
  center[1] = center[1] / points.length
  return center
}
function relocateCenter (points?:any[]) {
  const center = getPointsCenter(points)
  MapViewContainer.value.setCenter(center)
}
</script>

  <style lang="scss" scoped>

  .g-map-wrapper {
    height: 100%;
    width: 100%;

  }
  :deep(){
    .amap-info-content{
        padding: 1px;
    }
    .amap-info-close{
        display: none;
    }
  }

  </style>
